<script setup lang="ts">
import type {
  VoixMediaFieldInterface,
} from '@voix/types'
import { useImage } from '#imports'
import { computed, inject } from 'vue'

const props = defineProps<{
  field?: VoixMediaFieldInterface | string
  currentBreakpoint?: string
  background?: boolean
  breakpoint?: string
  loading?: 'lazy' | 'eager'
  debug?: boolean
}>()

const emit = defineEmits([
  'load',
])

// This is to detect if the parent is the first element in the loop so
// we can apply fetchpriority=high and remove Lazy Loading
const parentIsFirstElement = inject('parentIsFirstElement', false)

const renderFormat = computed(() => {
  // const media = currentMedia.value as currentMediaObjectInterface
  // if (media && media.type && media?.type === 'video')
  //   return 'Video'

  return 'Image'
})

function mediaLoaded() {
  emit('load', { src: props.field })
}

const mediaSource = computed(() => {
  if (!props.field)
    return null

  if (typeof props.field === 'object') {
    if (Array.isArray(props.field.value))
      return props.field.value[0]?.url || null // Extract the first item's URL if it's an array
    return props.field.value
  }

  return props.field
})

const nuxtImage = useImage()
const nuxtMediaSource = computed(() => {
  if (!props.field)
    return null

  let src = props.field
  if (typeof props.field === 'object')
    src = props.field.url

  return nuxtImage(src as string)
})
</script>

<template>
  <NuxtImg v-if="!background && mediaSource" :src="mediaSource" v-bind="$attrs" @load="mediaLoaded" />

  <slot
    v-else :image="nuxtMediaSource"
    v-bind="$attrs"
    :loading="parentIsFirstElement ? 'eager' : loading"
    :fetchpriority="parentIsFirstElement ? 'high' : 'auto'"
  />

  <div v-if="debug" class="space-y-4 mt-[25px] bg-white p-8 px-8 rounded-sm whitespace-pre-line break-words max-w-[100vw] overflow-x-scroll">
    <ClientOnly>
      <h1 class="text-2xl">
        Media String Debug
      </h1>
      <div>
        <strong>Source:</strong>
        <pre>{{ mediaSource }}</pre>
      </div>
      <div>
        <strong>Render Format:</strong>
        <pre>{{ renderFormat }}</pre>
      </div>
      <div>
        <strong>Background:</strong>
        <pre>{{ background }}</pre>
      </div>
      <div>
        <strong>Breakpoint:</strong>
        <pre>{{ breakpoint || 'Not defined' }}</pre>
      </div>
      <div>
        <strong>Parent is first element:</strong>
        <pre>{{ parentIsFirstElement }}</pre>
      </div>
    </ClientOnly>
  </div>
</template>
